import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../../../../foundation/altzero_base/base/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../../../../foundation/altzero_base/base/components/ui/card";
import { Badge } from "../../../../../foundation/altzero_base/base/components/ui/badge";
import { Edit, Trash2, Star, Eye, Check, Files, Loader2 } from "lucide-react";
import { TemplateDocument } from "../../../types/document-library";

interface TemplateCardProps {
  template: TemplateDocument;
  onPreview: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onSelect?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  isSelectionMode?: boolean;
  isDeleting?: boolean;
}

export function TemplateCard({
  template,
  onPreview,
  onEdit,
  onDelete,
  onSelect,
  onDuplicate,
  isSelectionMode,
  isDeleting = false,
}: TemplateCardProps) {
  // Check for default template using the correct property path
  const isDefault =
    (template.rawData?.metadata as any)?.isDefault ||
    (template as any).isDefault;

  return (
    <Card className="hover:shadow-md transition-shadow h-full flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <CardTitle className="text-lg truncate flex-1">
                {template.title}
              </CardTitle>
              {isDefault && (
                <Badge variant="secondary" className="text-xs flex-shrink-0">
                  <Star className="w-3 h-3 mr-1" />
                  Default
                </Badge>
              )}
            </div>
            <CardDescription className="line-clamp-2">
              {template.description}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            Created: {template.date}
          </div>
          {(template as any).category && (
            <Badge variant="outline" className="text-xs">
              {(template as any).category}
            </Badge>
          )}
          <div className="text-sm">
            <p className="font-medium mb-2">
              Sections ({template.sections.length}):
            </p>
            <div className="flex flex-wrap gap-1">
              {template.sections
                .slice(0, 4)
                .map((section: string, idx: number) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {section}
                  </Badge>
                ))}
              {template.sections.length > 4 && (
                <Badge variant="secondary" className="text-xs">
                  +{template.sections.length - 4} more
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="p-4 pt-0">
        {isSelectionMode ? (
          // Selection mode - show only preview and select buttons
          <div className="flex gap-2 w-full">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPreview(template.id)}
              className="gap-1 flex-shrink-0"
            >
              <Eye className="w-3 h-3" />
              Preview
            </Button>
            <Button
              size="sm"
              onClick={() => onSelect?.(template.id)}
              className="flex-1 gap-1 min-w-0"
            >
              <Check className="w-3 h-3" />
              Use This Template
            </Button>
          </div>
        ) : (
          // Normal mode - show appropriate buttons based on template type
          <div className="flex flex-wrap gap-2 w-full">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPreview(template.id)}
              className="gap-1 flex-shrink-0"
            >
              <Eye className="w-3 h-3" />
              Preview
            </Button>

            {/* Duplicate button for all templates */}
            {onDuplicate && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDuplicate(template.id)}
                className="gap-1 flex-shrink-0"
              >
                <Files className="w-3 h-3" />
                Duplicate
              </Button>
            )}

            {/* Custom templates: Show Edit and Delete buttons */}
            {!isDefault && onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(template.id)}
                className="gap-1 flex-shrink-0"
              >
                <Edit className="w-3 h-3" />
                Edit
              </Button>
            )}
            {!isDefault && onDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete(template.id)}
                disabled={isDeleting}
                className="gap-1 flex-shrink-0 text-red-600 hover:text-red-700 hover:border-red-300 disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
